/**
 * @fileoverview Local-first service for system-web violation code set raw artifact browsing.
 * @module system-web/violation-code-sets
 *
 * Responsibilities:
 * - Read vendor-native code set raw artifact rows from Dexie `code_set_raw_artifacts`
 * - Project artifact documents into grid-safe row models for the system-web library page
 * - Watch the local cache so the UI updates automatically when sync pulls new artifacts
 *
 * Dependencies:
 * - `@firesquirrel/local-db` for system Dexie access
 * - `dexie` liveQuery for local-first subscriptions
 *
 * Architecture:
 * - Reads are local-first from Dexie only
 * - The service does not call API routes directly
 * - Vendor-native system artifacts remain the source for the library page
 *
 * Notes:
 * - Tenant-specific ICC setup stays in the department detail Code Set tab
 * - This service intentionally exposes artifact/library semantics, not tenant-runtime violation semantics
 */

import { getSystemDb } from '@firesquirrel/local-db';
import { liveQuery } from 'dexie';

/**
 * @contract CodeSetRawArtifactChapter
 *
 * Purpose:
 * - Represents a normalized chapter summary stored inside a cached vendor artifact.
 *
 * Fields:
 * - chapterId: Stable chapter identifier.
 * - chapterNumber: Display chapter number.
 * - title: Chapter title.
 * - parentChapterId: Optional parent chapter identifier.
 *
 * Rules:
 * - Derived cache metadata only; not a tenant-runtime contract.
 *
 * Sync:
 * - Pulled from system MongoDB into Dexie and served locally in system-web.
 */
export interface CodeSetRawArtifactChapter {
  chapterId: string;
  chapterNumber: string;
  title: string;
  parentChapterId?: string;
}

/**
 * @contract CodeSetRawArtifactViolation
 *
 * Purpose:
 * - Represents a normalized section/violation summary stored inside a cached vendor artifact.
 *
 * Fields:
 * - violationId: Stable vendor section identifier.
 * - sectionCode: Display section code.
 * - title: Short section title.
 * - description: Optional long-form content snippet.
 * - chapterId: Parent chapter identifier.
 * - tags: Optional vendor tags.
 *
 * Rules:
 * - Used only for system library preview and cache metadata display.
 *
 * Sync:
 * - Pulled from system MongoDB into Dexie and served locally in system-web.
 */
export interface CodeSetRawArtifactViolation {
  violationId: string;
  sectionCode: string;
  title: string;
  description?: string;
  chapterId: string;
  tags?: string[];
}

/**
 * @contract SystemViolationCodeSetArtifact
 *
 * Purpose:
 * - Represents one cached vendor-native code set artifact in system scope.
 *
 * Fields:
 * - _id: Artifact identifier.
 * - cacheKey: Cache identity combining vendor/book/printing.
 * - vendorId: Vendor identifier.
 * - bookId: Vendor book identifier.
 * - requestedBookId: Requested vendor book identifier.
 * - printing: Resolved printing/edition.
 * - requestedPrinting: Requested printing/edition.
 * - title: Book title.
 * - status: Artifact cache status.
 * - sourcePayload: Vendor-native payload summary.
 * - normalized: Derived normalized cache preview.
 * - chapterCount: Cached chapter count.
 * - violationCount: Cached violation count.
 * - downloadedAt: Download completion timestamp.
 * - createdAt: Artifact creation timestamp.
 * - updatedAt: Artifact update timestamp.
 *
 * Rules:
 * - Represents system library artifacts, not tenant-level selected books.
 *
 * Sync:
 * - Server-authoritative raw artifacts are pulled into Dexie for local-first browsing.
 */
/**
 * @contract CodeSetChapterManifestEntry
 *
 * Purpose:
 * - Blob-cache pointer for one chapter in the vendor-native manifest. Each
 *   entry points at a gzipped blob in the `code-set-cache` container and
 *   carries the SHA-256 hash used by Verify Integrity.
 *
 * Fields:
 * - chapterId: Vendor chapter id.
 * - ordinal: Display ordinal (e.g. "9" or "3.1").
 * - title: Plain-text chapter title.
 * - sectionCount: Count of sections in the chapter blob.
 * - blobPath: Path inside the code-set-cache container.
 * - blobUri: Fully-qualified blob URL (admin-only; never shown to tenants).
 * - bytes: Size of the gzipped blob.
 * - sha256: Hex SHA-256 of the UNCOMPRESSED payload.
 *
 * Sync:
 * - Synced as part of the parent artifact row; never consumed by tenant-web.
 */
export interface CodeSetChapterManifestEntry {
  chapterId: string;
  ordinal: string;
  title: string;
  sectionCount: number;
  blobPath: string;
  blobUri?: string;
  bytes: number;
  sha256: string;
}

export interface SystemViolationCodeSetArtifact {
  _id: string;
  cacheKey: string;
  vendorId: string;
  bookId: string;
  requestedBookId?: string;
  printing?: string;
  requestedPrinting?: string;
  title: string;
  edition?: string;
  status?: string;
  // Blob-cache shape (Step A.2 gap-closure 2026-05-01).
  catalog?: { book?: Record<string, unknown> | null };
  chapterManifest?: CodeSetChapterManifestEntry[];
  catalogBlobPath?: string;
  chaptersBlobPath?: string;
  totalBytes?: number;
  lastVerifiedAt?: string;
  verifyStatus?: string;
  cacheProgress?: {
    phase?: string;
    percent?: number;
    message?: string;
    processedRecords?: number;
    totalRecords?: number;
    updatedAt?: string | Date;
  };
  cacheProgressLog?: Array<{
    phase?: string;
    percent?: number;
    message?: string;
    processedRecords?: number;
    totalRecords?: number;
    updatedAt?: string | Date;
  }>;
  // Legacy pre-blob-cache shape (kept for backwards read compatibility on
  // artifacts written before Step A.2 landed; new rows do NOT populate these).
  sourcePayload?: Record<string, unknown>;
  normalized?: {
    bookId?: string;
    title?: string;
    edition?: string;
    vendorId?: string;
    chapterCount?: number;
    violationCount?: number;
    chapters?: CodeSetRawArtifactChapter[];
    violations?: CodeSetRawArtifactViolation[];
  };
  chapterCount?: number;
  violationCount?: number;
  downloadedAt?: string;
  createdAt?: string;
  updatedAt?: string;
}

/**
 * @contract SystemViolationCodeSetArtifactRow
 *
 * Purpose:
 * - Represents the grid/list projection for one cached vendor-native code set artifact.
 *
 * Fields:
 * - _id: Artifact identifier.
 * - vendorId: Vendor identifier.
 * - vendorLabel: Display label for the vendor.
 * - title: Display book title.
 * - edition: Display edition/printing.
 * - bookId: Vendor book id.
 * - status: Artifact cache status.
 * - chapterCount: Cached chapter count.
 * - violationCount: Cached violation count.
 * - downloadedAt: Download timestamp.
 * - updatedAt: Last update timestamp.
 * - cacheKey: Cache identity.
 *
 * Rules:
 * - Tailored for grid rendering only.
 *
 * Sync:
 * - Derived locally from pulled artifact records.
 */
export interface SystemViolationCodeSetArtifactRow {
  _id: string;
  vendorId: string;
  vendorLabel: string;
  title: string;
  edition: string;
  bookId: string;
  status: string;
  chapterCount: number;
  violationCount: number;
  totalBytes: number;
  downloadedAt?: string;
  lastVerifiedAt?: string;
  updatedAt?: string;
  cacheKey: string;
  cacheProgress?: SystemViolationCodeSetArtifact['cacheProgress'];
}

function toVendorLabel(vendorId: string): string {
  if (vendorId === 'icc') return 'ICC Digital Codes';
  if (vendorId === 'nfpa') return 'NFPA LiNK';
  return vendorId ? vendorId.toUpperCase() : 'Unknown Vendor';
}

function toIsoDate(value: unknown): string | undefined {
  if (typeof value === 'string' && value) return value;
  if (value instanceof Date) return value.toISOString();
  return undefined;
}

function normalizeArtifact(doc: Record<string, unknown>): SystemViolationCodeSetArtifact {
  return {
    _id: String(doc._id ?? ''),
    cacheKey: String(doc.cacheKey ?? ''),
    vendorId: String(doc.vendorId ?? ''),
    bookId: String(doc.bookId ?? ''),
    requestedBookId: typeof doc.requestedBookId === 'string' ? doc.requestedBookId : undefined,
    printing: typeof doc.printing === 'string' ? doc.printing : undefined,
    requestedPrinting: typeof doc.requestedPrinting === 'string' ? doc.requestedPrinting : undefined,
    title: String(doc.title ?? doc.bookId ?? 'Untitled Code Set'),
    edition: typeof doc.edition === 'string' ? doc.edition : undefined,
    status: typeof doc.status === 'string' ? doc.status : undefined,
    catalog: (doc.catalog as SystemViolationCodeSetArtifact['catalog']) ?? undefined,
    chapterManifest: (doc.chapterManifest as CodeSetChapterManifestEntry[] | undefined) ?? undefined,
    catalogBlobPath: typeof doc.catalogBlobPath === 'string' ? doc.catalogBlobPath : undefined,
    chaptersBlobPath: typeof doc.chaptersBlobPath === 'string' ? doc.chaptersBlobPath : undefined,
    totalBytes: typeof doc.totalBytes === 'number' ? doc.totalBytes : undefined,
    lastVerifiedAt: toIsoDate(doc.lastVerifiedAt),
    verifyStatus: typeof doc.verifyStatus === 'string' ? doc.verifyStatus : undefined,
    cacheProgress: (doc.cacheProgress as SystemViolationCodeSetArtifact['cacheProgress']) ?? undefined,
    cacheProgressLog: (doc.cacheProgressLog as SystemViolationCodeSetArtifact['cacheProgressLog']) ?? undefined,
    sourcePayload: (doc.sourcePayload as Record<string, unknown> | undefined) ?? {},
    normalized: (doc.normalized as SystemViolationCodeSetArtifact['normalized']) ?? undefined,
    chapterCount: typeof doc.chapterCount === 'number' ? doc.chapterCount : undefined,
    violationCount: typeof doc.violationCount === 'number' ? doc.violationCount : undefined,
    downloadedAt: toIsoDate(doc.downloadedAt),
    createdAt: toIsoDate(doc.createdAt),
    updatedAt: toIsoDate(doc.updatedAt),
  };
}

function deriveChapterCount(artifact: SystemViolationCodeSetArtifact): number {
  return (
    artifact.chapterCount
      ?? artifact.chapterManifest?.length
      ?? artifact.normalized?.chapterCount
      ?? 0
  );
}

function deriveViolationCount(artifact: SystemViolationCodeSetArtifact): number {
  if (typeof artifact.violationCount === 'number') return artifact.violationCount;
  if (artifact.chapterManifest) {
    return artifact.chapterManifest.reduce((sum, entry) => sum + (entry.sectionCount ?? 0), 0);
  }
  return artifact.normalized?.violationCount ?? 0;
}

function toRow(artifact: SystemViolationCodeSetArtifact): SystemViolationCodeSetArtifactRow {
  const progress = artifact.cacheProgress;
  const progressPercent = typeof progress?.percent === 'number' ? ` · ${Math.round(progress.percent)}%` : '';
  const edition = progress
    ? `${artifact.edition ?? artifact.printing ?? artifact.requestedPrinting ?? artifact.normalized?.edition ?? '—'} · ${progress.message ?? progress.phase ?? artifact.status ?? 'syncing'}${progressPercent}`
    : artifact.edition ?? artifact.printing ?? artifact.requestedPrinting ?? artifact.normalized?.edition ?? '—';
  return {
    _id: artifact._id,
    vendorId: artifact.vendorId,
    vendorLabel: toVendorLabel(artifact.vendorId),
    title: artifact.title,
    edition,
    bookId: artifact.bookId,
    status: artifact.status ?? 'unknown',
    chapterCount: deriveChapterCount(artifact),
    violationCount: deriveViolationCount(artifact),
    totalBytes: artifact.totalBytes ?? 0,
    downloadedAt: artifact.downloadedAt,
    lastVerifiedAt: artifact.lastVerifiedAt,
    updatedAt: artifact.updatedAt,
    cacheKey: artifact.cacheKey,
    cacheProgress: artifact.cacheProgress,
  };
}

function sortArtifacts(rows: SystemViolationCodeSetArtifactRow[]): SystemViolationCodeSetArtifactRow[] {
  return [...rows].sort((left, right) => {
    const leftDate = left.downloadedAt ?? left.updatedAt ?? '';
    const rightDate = right.downloadedAt ?? right.updatedAt ?? '';
    return rightDate.localeCompare(leftDate) || left.title.localeCompare(right.title);
  });
}

/**
 * @process ListSystemViolationCodeSetArtifacts
 *
 * Steps:
 * 1. Read raw artifact documents from Dexie.
 * 2. Normalize documents into typed artifact records.
 * 3. Project artifact records into grid-safe rows.
 * 4. Sort rows by newest download/update.
 *
 * Inputs:
 * - Local `code_set_raw_artifacts` Dexie collection.
 *
 * Outputs:
 * - Sorted grid rows for the system-web library page.
 *
 * Failure Handling:
 * - Returns an empty list when no artifacts are available.
 *
 * Idempotency:
 * - REQUIRED
 */
export async function listSystemViolationCodeSetArtifacts(): Promise<SystemViolationCodeSetArtifactRow[]> {
  const db = await getSystemDb();
  const docs = await db.code_set_raw_artifacts.toArray();
  const rows = (docs as Record<string, unknown>[])
    .filter((doc) => doc?._deleted !== true)
    .map(normalizeArtifact)
    .map(toRow);
  return sortArtifacts(rows);
}

/**
 * @process GetSystemViolationCodeSetArtifact
 *
 * Steps:
 * 1. Look up one artifact by id from Dexie.
 * 2. Normalize the artifact document.
 * 3. Return null when the artifact is absent or deleted.
 *
 * Inputs:
 * - Artifact id.
 *
 * Outputs:
 * - Full artifact detail for slideout rendering.
 *
 * Failure Handling:
 * - Returns null when the requested artifact is not in the local cache.
 *
 * Idempotency:
 * - REQUIRED
 */
export async function getSystemViolationCodeSetArtifact(id: string): Promise<SystemViolationCodeSetArtifact | null> {
  const db = await getSystemDb();
  const doc = await db.code_set_raw_artifacts.get(id);
  if (!doc || (doc as Record<string, unknown>)._deleted === true) {
    return null;
  }
  return normalizeArtifact(doc as Record<string, unknown>);
}

/**
 * @process WatchSystemViolationCodeSetArtifacts
 *
 * Steps:
 * 1. Open the system Dexie database.
 * 2. Subscribe to a live query over `code_set_raw_artifacts`.
 * 3. Project updates into sorted grid rows and emit them.
 *
 * Inputs:
 * - Next/error callbacks.
 *
 * Outputs:
 * - Unsubscribe function.
 *
 * Failure Handling:
 * - Forwards subscription/open errors to the provided error callback.
 *
 * Idempotency:
 * - NOT REQUIRED
 */
export function watchSystemViolationCodeSetArtifacts(
  onNext: (rows: SystemViolationCodeSetArtifactRow[]) => void,
  onError?: (error: unknown) => void,
): () => void {
  let subscription: { unsubscribe: () => void } | null = null;
  let cancelled = false;

  getSystemDb()
    .then((db) => {
      if (cancelled) return;
      subscription = liveQuery(async () => {
        const docs = await db.code_set_raw_artifacts.toArray();
        const rows = (docs as Record<string, unknown>[])
          .filter((doc) => doc?._deleted !== true)
          .map(normalizeArtifact)
          .map(toRow);
        return sortArtifacts(rows);
      }).subscribe({
        next: (rows) => onNext(rows),
        error: (error) => {
          onError?.(error);
        },
      });
    })
    .catch((error) => {
      if (!cancelled) {
        onError?.(error);
      }
    });

  return () => {
    cancelled = true;
    subscription?.unsubscribe();
  };
}
